import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Collapse } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { PermissionArea, Text } from '@pbt/pbt-ui-components'
import { Warning as WarningIcon } from '@pbt/pbt-ui-components/src/icons'

import { getCRUDByArea } from '~/store/reducers/auth'
import { getMemberIsLoading } from '~/store/reducers/members'

const useStyles = makeStyles(
  (theme) => ({
    warning: {
      color: 'white',
      backgroundColor: theme.colors.alertLabelError,
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: 3,
      gap: theme.spacing(1),
    },
    warningIcon: {
      width: '1.4rem',
      height: '1.4rem',
    },
    button: {
      textDecoration: 'underline',
      color: 'inherit',
      padding: 0,
      textTransform: 'initial',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      minWidth: 'auto',
      lineHeight: 'inherit',
      verticalAlign: 'inherit',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
  { name: 'DoctorLicenseStateWarning' },
)

interface DoctorLicenseStateWarningProps {
  doctorId: string
  hasMatchingStateLicense: boolean | null // null - no doctor selected or loading
}

const DoctorLicenseStateWarning = ({
  doctorId,
  hasMatchingStateLicense,
}: DoctorLicenseStateWarningProps) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { t } = useTranslation(['Common'])

  const memberIsLoading = useSelector(getMemberIsLoading)
  const personPermissions = useSelector(getCRUDByArea(PermissionArea.PERSON))

  const navigateToAdminLicensesSection = () => {
    navigate(`/admin/general/members/${doctorId}`)
  }

  const isHidden =
    hasMatchingStateLicense !== false || memberIsLoading || !doctorId

  return (
    <Collapse unmountOnExit in={!isHidden}>
      <Text
        strong
        className={classes.warning}
        mb={2}
        px={1}
        py={0.25}
        variant="body2"
      >
        <WarningIcon className={classes.warningIcon} />
        <span>{t('Common:NO_LICENSE_NUMBER_WITH_STATE')} </span>
        {personPermissions.update && (
          <Button
            className={classes.button}
            size="small"
            onClick={navigateToAdminLicensesSection}
          >
            {t('Common:ADD_NOW')}
          </Button>
        )}
      </Text>
    </Collapse>
  )
}

export default DoctorLicenseStateWarning
